/**
 * AssignedRecruitersDisplay Component
 * Displays assigned recruiters with avatar stack and names
 */

import React from 'react'
import { UserCog } from 'lucide-react'
import type { JobAssignment } from '../../services/api'

interface AssignedRecruitersDisplayProps {
  recruiters: JobAssignment[]
  maxAvatars?: number
  maxNames?: number
  showManageButton?: boolean
  onManageClick?: () => void
  size?: 'sm' | 'xs'
}

/**
 * Extract initials from a name
 * Examples: "John Doe" -> "JD", "Alice" -> "A", "Bob Smith Jr" -> "BS"
 */
export const getInitials = (name: string): string => {
  if (!name || name.trim() === '') return '?'
  
  const parts = name.trim().split(/\s+/)
  
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase()
  }
  
  // Take first letter of first name and first letter of last name
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
}

/**
 * Generate a consistent color from a name using a simple hash function
 * Returns a Tailwind color class
 */
export const getColor = (name: string): string => {
  if (!name) return 'bg-gray-500'
  
  // Simple hash function
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  
  // Color palette - professional and accessible colors
  const colors = [
    'bg-blue-500',
    'bg-green-500',
    'bg-purple-500',
    'bg-pink-500',
    'bg-indigo-500',
    'bg-teal-500',
    'bg-orange-500',
    'bg-cyan-500',
  ]
  
  const index = Math.abs(hash) % colors.length
  return colors[index]
}

export const AssignedRecruitersDisplay: React.FC<AssignedRecruitersDisplayProps> = ({
  recruiters,
  maxAvatars = 3,
  maxNames = 2,
  showManageButton = false,
  onManageClick,
  size = 'sm',
}) => {
  const nameTextClass = size === 'xs' ? 'text-xs' : 'text-sm'

  if (!recruiters || recruiters.length === 0) {
    return (
      <div className="flex items-center gap-2">
        <div className={`${nameTextClass} text-gray-500 italic`}>
          No recruiters assigned
        </div>
        {showManageButton && onManageClick && (
          <button
            onClick={onManageClick}
            className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
            title="Manage recruiters"
          >
            <UserCog className="w-4 h-4" />
          </button>
        )}
      </div>
    )
  }

  const visibleAvatars = recruiters.slice(0, maxAvatars)
  const remainingCount = recruiters.length - maxAvatars
  const visibleNames = recruiters.slice(0, maxNames)
  const remainingNamesCount = recruiters.length - maxNames

  // Compact table cells (size="xs") show only the avatar stack — each
  // avatar's native title attribute reveals the assignee's name and email
  // on hover, so the column stays narrow without losing that information.
  if (size === 'xs') {
    return (
      <div className="min-w-0">
        <div className="flex -space-x-1.5">
          {visibleAvatars.map((recruiter, index) => (
            <div
              key={recruiter.id}
              className={`
                relative inline-flex items-center justify-center
                w-6 h-6 rounded-full border-2 border-white
                text-white text-[10px] font-semibold
                ${getColor(recruiter.name)}
                hover:z-10 transition-transform hover:scale-110
                cursor-default
              `}
              title={`${recruiter.name} (${recruiter.email})`}
              style={{ zIndex: visibleAvatars.length - index }}
            >
              {getInitials(recruiter.name)}
            </div>
          ))}

          {remainingCount > 0 && (
            <div
              className="
                relative inline-flex items-center justify-center
                w-6 h-6 rounded-full border-2 border-white
                bg-gray-400 text-white text-[10px] font-semibold
                hover:z-10 transition-transform hover:scale-110
                cursor-default
              "
              title={`+${remainingCount} more: ${recruiters.slice(maxAvatars).map((r) => r.name).join(', ')}`}
              style={{ zIndex: 0 }}
            >
              +{remainingCount}
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-3 min-w-0">
      {/* Avatar Stack */}
      <div className="flex -space-x-2 flex-shrink-0">
        {visibleAvatars.map((recruiter, index) => (
          <div
            key={recruiter.id}
            className={`
              relative inline-flex items-center justify-center
              w-8 h-8 rounded-full border-2 border-white
              text-white text-xs font-semibold
              ${getColor(recruiter.name)}
              hover:z-10 transition-transform hover:scale-110
              cursor-default
            `}
            title={`${recruiter.name} (${recruiter.email})`}
            style={{ zIndex: visibleAvatars.length - index }}
          >
            {getInitials(recruiter.name)}
          </div>
        ))}
        
        {remainingCount > 0 && (
          <div
            className="
              relative inline-flex items-center justify-center
              w-8 h-8 rounded-full border-2 border-white
              bg-gray-400 text-white text-xs font-semibold
              hover:z-10 transition-transform hover:scale-110
              cursor-default
            "
            title={`+${remainingCount} more: ${recruiters.slice(maxAvatars).map(r => r.name).join(', ')}`}
            style={{ zIndex: 0 }}
          >
            +{remainingCount}
          </div>
        )}
      </div>

      {/* Names Display */}
      <div
        className={`${nameTextClass} text-gray-700 truncate min-w-0`}
        title={recruiters.map((recruiter) => recruiter.name).join(', ')}
      >
        {visibleNames.map((recruiter, index) => (
          <span key={recruiter.id}>
            {recruiter.name}
            {index < visibleNames.length - 1 && ', '}
          </span>
        ))}
        {remainingNamesCount > 0 && (
          <span className="text-gray-500">
            {' '}+{remainingNamesCount} more
          </span>
        )}
      </div>

      {/* Manage Button */}
      {showManageButton && onManageClick && (
        <button
          onClick={onManageClick}
          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
          title="Manage recruiters"
        >
          <UserCog className="w-4 h-4" />
        </button>
      )}
    </div>
  )
}
